import React, {useState, useEffect, useRef} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {useSourceTracking} from '../../hooks/useSourceTracking';
import {cmsService} from '../../services/cmsService';
import SafeIcon from '../../common/SafeIcon';
import {FiCheckCircle, FiAlertCircle, FiCheck, FiChevronDown} from 'react-icons/fi';

const scrollToContact = () => {
  const contactSection = document.getElementById('contact');
  if (contactSection) {
    contactSection.scrollIntoView({behavior: 'smooth'});
  }
};

const Contact = ({selectedPackage}) => {
  const tracking = useSourceTracking();
  const [formState, setFormState] = useState('idle');
  const [submitMode, setSubmitMode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formStartedAt, setFormStartedAt] = useState(null);
  const [progressIndex, setProgressIndex] = useState(0);
  const formRef = useRef(null);
  const [pricing, setPricing] = useState([]);
  const [mathA, setMathA] = useState(0);
  const [mathB, setMathB] = useState(0);
  
  const consentTextStr = "I agree to submit my enquiry to BPA Solutions, and I consent to receive relevant BPA Solutions tips, tools, updates, and service information. I understand I can unsubscribe at any time.";

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    brandName: '',
    website: '',
    packageInterest: '',
    jewelleryType: '',
    neededService: '',
    timeline: '',
    message: '',
    preferredContact: 'Email',
    mathAnswer: '',
    marketingConsent: false,
    company_website_confirm: ''
  });

  const stages = [
    "Securing your jewellery visual enquiry",
    "Preparing your brand and package details",
    "Recording your request in the BPA Solutions lead register",
    "Sending your premium acknowledgement email",
    "Notifying Anish and the BPA Solutions team",
    "Finalising your enquiry"
  ];

  const generateMathChallenge = () => {
    setMathA(Math.floor(Math.random() * 9) + 1);
    setMathB(Math.floor(Math.random() * 9) + 1);
    setFormData(prev => ({...prev, mathAnswer: ''}));
  };

  useEffect(() => {
    const loadPricing = async () => {
      const data = await cmsService.fetchData();
      setPricing(data.pricing || []);
    };
    loadPricing();
    generateMathChallenge();
  }, []);

  useEffect(() => {
    if (selectedPackage) {
      setFormData(prev => ({...prev, packageInterest: selectedPackage}));
    }
  }, [selectedPackage]);

  const handleInputChange = (e) => {
    if (!formStartedAt) setFormStartedAt(Date.now());
    const {id, value, type, checked} = e.target;
    setFormData(prev => ({...prev, [id]: type === 'checkbox' ? checked : value}));
  };

  const validate = () => {
    const API_URL = import.meta.env.VITE_JEWELLERY_CMS_API_URL;
    if (!API_URL || API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
      setErrorMessage('The enquiry endpoint is not configured yet. Please email support@bpasolutions.au or try again shortly.');
      return false;
    }
    if (formData.company_website_confirm) return false;
    if (!formData.name || !formData.email || !formData.brandName) {
      setErrorMessage('Please complete all required fields.');
      return false;
    }
    if (!formData.marketingConsent) {
      setErrorMessage('You must agree to the consent terms to submit your enquiry.');
      return false;
    }
    if (parseInt(formData.mathAnswer) !== (mathA + mathB)) {
      setErrorMessage('Please solve the simple math challenge correctly.');
      return false;
    }
    const timeToSubmit = Date.now() - (formStartedAt || Date.now());
    if (timeToSubmit < 3000) {
      setErrorMessage('Form submitted too quickly. Please review your details and try again.');
      return false;
    }
    const spamKeywords = ['crypto', 'viagra', 'bitcoin', 'investment opportunity'];
    if (spamKeywords.some(key => formData.message.toLowerCase().includes(key))) {
      setErrorMessage('Your message contains flagged keywords. Please revise.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formState === 'submitting') return;
    setErrorMessage('');
    if (!validate()) {
      if (!errorMessage && formData.company_website_confirm) setFormState('idle');
      return;
    }
    setFormState('submitting');
    setProgressIndex(0);
    const timeToSubmitSeconds = Math.floor((Date.now() - (formStartedAt || Date.now())) / 1000);

    // Get selected package details
    const selectedPkgData = pricing.find(p => p.tier === formData.packageInterest || p.packageID === formData.packageInterest);
    
    const parseFeatures = (features) => {
      if (Array.isArray(features)) return features;
      if (typeof features === 'string') return features.split(',').map(f => f.trim()).filter(Boolean);
      return [];
    };

    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      brandName: formData.brandName,
      website: formData.website,
      packageInterest: formData.packageInterest,
      jewelleryType: formData.jewelleryType,
      neededService: formData.neededService,
      timeline: formData.timeline,
      message: formData.message,
      preferredContact: formData.preferredContact,
      pageURL: tracking.pageURL || window.location.href,
      referrer: tracking.referrer || document.referrer || 'direct',
      source: tracking.source || 'Jewellery Landing Page',
      utm_source: tracking.utm_source || '',
      utm_medium: tracking.utm_medium || '',
      utm_campaign: tracking.utm_campaign || '',
      company_website_confirm: formData.company_website_confirm,
      mathA: mathA,
      mathB: mathB,
      mathQuestion: `${mathA} + ${mathB}`,
      mathAnswer: formData.mathAnswer,
      timeToSubmitSeconds: timeToSubmitSeconds,
      marketingConsent: formData.marketingConsent ? "Yes" : "No",
      consentText: consentTextStr,
      // Package details for internal notification
      packageID: selectedPkgData?.packageID || '',
      packagePrice: selectedPkgData?.priceText || '',
      packageTag: selectedPkgData?.tag || '',
      packageDescription: selectedPkgData?.description || '',
      packageIncludes: selectedPkgData?.features ? parseFeatures(selectedPkgData.features).join(';') : '',
      packageCheckoutUrl: selectedPkgData?.checkoutUrl || ''
    };

    const runStagedProgress = new Promise(resolve => {
      let currentStage = 0;
      const interval = setInterval(() => {
        currentStage++;
        if (currentStage < stages.length) {
          setProgressIndex(currentStage);
        } else {
          clearInterval(interval);
          resolve();
        }
      }, 800);
    });

    const [result] = await Promise.all([
      cmsService.submitLead(payload),
      runStagedProgress
    ]);

    if (result.success) {
      setSubmitMode(result.mode);
      setFormState('success');
      // Reset form and regenerate math
      setFormData({
        name: '',
        email: '',
        phone: '',
        brandName: '',
        website: '',
        packageInterest: '',
        jewelleryType: '',
        neededService: '',
        timeline: '',
        message: '',
        preferredContact: 'Email',
        mathAnswer: '',
        marketingConsent: false,
        company_website_confirm: ''
      });
      generateMathChallenge();
    } else {
      setFormState('idle');
      setErrorMessage(result.error || 'A network issue occurred. Please try again or email us directly.');
      generateMathChallenge();
    }
  };

  const parseFeatures = (features) => {
    if (Array.isArray(features)) return features;
    if (typeof features === 'string') return features.split(',').map(f => f.trim()).filter(Boolean);
    return [];
  };

  const selectedPkgData = pricing.find(p => p.tier === formData.packageInterest || p.packageID === formData.packageInterest);

  // Dynamic packages from CMS
  const packageOptions = pricing.map(p => p.tier);
  if (selectedPackage && !packageOptions.includes(selectedPackage)) {
    packageOptions.push(selectedPackage);
  }
  
  // Fallback options if CMS is totally empty
  if (packageOptions.length === 0) {
    packageOptions.push('Starter Proof Pack', 'Product Page Upgrade', 'Collection Launch Pack', 'Monthly Visual Retainer');
  }

  return (
    <section id="contact" className="py-32 relative">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="luxury-panel p-10 md:p-16 relative overflow-hidden bg-white/80 backdrop-blur-xl ring-1 ring-white/50 luxury-hover-lift">
          <AnimatePresence mode="wait">
            {/* SUCCESS STATE */}
            {formState === 'success' && (
              <motion.div
                key="success"
                initial={{opacity: 0, scale: 0.95}}
                animate={{opacity: 1, scale: 1}}
                exit={{opacity: 0}}
                className="text-center py-12"
              >
                <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-8 shadow-goldGlow">
                  <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-gold" />
                </div>
                <h2 className="text-4xl luxury-serif-heading mb-4">
                  Inquiry received
                </h2>
                <p className="text-muted font-light max-w-md mx-auto leading-relaxed">
                  Thank you. Your jewellery visuals enquiry has been received. We have also sent an acknowledgement with the details of your request to your inbox. Please check your inbox in the next few minutes, and also check your spam or junk folder just in case your email filter places it there.
                </p>
                <button onClick={() => setFormState('idle')} className="mt-10 text-xs uppercase tracking-widest text-gold font-bold hover:text-espresso transition-colors">
                  Send another inquiry
                </button>
              </motion.div>
            )}

            {/* STAGED PROGRESS UX */}
            {formState === 'submitting' && (
              <motion.div
                key="submitting"
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                className="py-16 text-center"
              >
                <div className="luxury-section-kicker justify-center mb-8">
                  <span>BPA Solutions Enquiry Concierge</span>
                </div>
                <div className="max-w-md mx-auto">
                  <h3 className="text-2xl luxury-serif-heading mb-10 h-16 flex items-center justify-center transition-all duration-300">
                    {stages[progressIndex]}...
                  </h3>
                  <div className="w-full h-1 bg-champagne overflow-hidden relative rounded-full">
                    <motion.div
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-gold to-goldDark"
                      initial={{width: '0%'}}
                      animate={{width: `${((progressIndex + 1) / stages.length) * 100}%`}}
                      transition={{duration: 0.8, ease: "easeInOut"}}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* IDLE FORM STATE */}
            {formState === 'idle' && (
              <motion.div
                key="form"
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
              >
                <div className="text-center mb-16">
                  <div className="luxury-section-kicker justify-center mb-6">
                    <span>Enquire</span>
                  </div>
                  <h2 className="text-4xl md:text-5xl luxury-serif-heading mb-4">Enquire About a Visual Proposal</h2>
                  <p className="font-light text-muted">Tell us about your jewellery brand, selected package and visual content goals.</p>
                </div>

                {/* PREMIUM PACKAGE PREVIEW */}
                <AnimatePresence>
                  {selectedPkgData && (
                    <motion.div
                      initial={{opacity: 0, height: 0}}
                      animate={{opacity: 1, height: 'auto'}}
                      exit={{opacity: 0, height: 0}}
                      className="mb-12 overflow-hidden"
                    >
                      <div className="p-8 border border-gold bg-paper rounded-soft shadow-luxurySoft relative luxury-hover-lift">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="luxury-serif-heading text-2xl">{selectedPkgData.tier}</h4>
                          {selectedPkgData.tag && (
                            <span className="bg-gradient-to-r from-gold to-champagne text-espressoDeep text-[10px] uppercase tracking-widest px-4 py-1 rounded-full font-bold shadow-sm">
                              {selectedPkgData.tag}
                            </span>
                          )}
                        </div>
                        <p className="text-gold font-serif mb-4 text-xl">{selectedPkgData.priceText}</p>
                        <p className="text-sm font-light text-muted mb-6 leading-relaxed">{selectedPkgData.description}</p>
                        <ul className="space-y-3 mb-6">
                          {parseFeatures(selectedPkgData.features).map((f, i) => (
                            <li key={i} className="flex items-start text-xs font-medium text-espresso">
                              <SafeIcon icon={FiCheck} className="text-gold mr-2 mt-0.5 flex-shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                        <div className="text-[10px] font-bold text-muted uppercase tracking-widest pt-5 border-t border-champagne">
                          {selectedPkgData.checkoutUrl ? "Checkout link available for this package." : "BPA Solutions will confirm the best next step after reviewing your enquiry."}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <form className="space-y-10" onSubmit={handleSubmit} ref={formRef}>
                  <div className="hidden">
                    <input
                      type="text"
                      id="company_website_confirm"
                      value={formData.company_website_confirm}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="relative">
                      <input
                        type="text"
                        id="name"
                        required
                        placeholder=" "
                        value={formData.name}
                        onChange={handleInputChange}
                        className="peer w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso"
                      />
                      <label htmlFor="name" className="absolute left-0 -top-3.5 text-[10px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-[10px] peer-focus:text-gold font-medium">
                        Full Name *
                      </label>
                    </div>
                    <div className="relative">
                      <input
                        type="email"
                        id="email"
                        required
                        placeholder=" "
                        value={formData.email}
                        onChange={handleInputChange}
                        className="peer w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso"
                      />
                      <label htmlFor="email" className="absolute left-0 -top-3.5 text-[10px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-[10px] peer-focus:text-gold font-medium">
                        Email Address *
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="relative">
                      <input
                        type="text"
                        id="brandName"
                        required
                        placeholder=" "
                        value={formData.brandName}
                        onChange={handleInputChange}
                        className="peer w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso"
                      />
                      <label htmlFor="brandName" className="absolute left-0 -top-3.5 text-[10px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-[10px] peer-focus:text-gold font-medium">
                        Jewellery Brand Name *
                      </label>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        id="phone"
                        placeholder=" "
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="peer w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso"
                      />
                      <label htmlFor="phone" className="absolute left-0 -top-3.5 text-[10px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-[10px] peer-focus:text-gold font-medium">
                        Phone Number
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="relative">
                      <input
                        type="url"
                        id="website"
                        placeholder=" "
                        value={formData.website}
                        onChange={handleInputChange}
                        className="peer w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso"
                      />
                      <label htmlFor="website" className="absolute left-0 -top-3.5 text-[10px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-[10px] peer-focus:text-gold font-medium">
                        Website or Store Link
                      </label>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        id="jewelleryType"
                        placeholder=" "
                        value={formData.jewelleryType}
                        onChange={handleInputChange}
                        className="peer w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso"
                      />
                      <label htmlFor="jewelleryType" className="absolute left-0 -top-3.5 text-[10px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-[10px] peer-focus:text-gold font-medium">
                        Jewellery Type
                      </label>
                      <p className="text-xs text-muted font-light mt-2">E.g. earrings, rings, necklaces, bracelets or mixed collection.</p>
                    </div>
                  </div>

                  {/* UPDATED DROPDOWN ROW - Fixed overflow and label sizing */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                    <div className="relative luxury-select-wrap min-w-0">
                      <select
                        id="neededService"
                        value={formData.neededService}
                        onChange={handleInputChange}
                        className="luxury-select-control w-full appearance-none bg-paper border border-champagne rounded-soft px-4 py-4 text-espresso font-light focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold shadow-luxurySoft pr-12"
                      >
                        <option value="">Select Service</option>
                        <option value="Model-wearing images">Model-wearing images</option>
                        <option value="Try-on style videos">Try-on style videos</option>
                        <option value="Product page visuals">Product page visuals</option>
                        <option value="Full campaign visuals">Full campaign visuals</option>
                        <option value="Not sure yet">Not sure yet</option>
                      </select>
                      <label className="absolute left-4 -top-4 text-[12px] uppercase tracking-[0.08em] text-muted font-medium">
                        Primary Need
                      </label>
                      <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gold pointer-events-none" />
                    </div>
                    <div className="relative luxury-select-wrap min-w-0">
                      <select
                        id="packageInterest"
                        value={formData.packageInterest}
                        onChange={handleInputChange}
                        className="luxury-select-control w-full appearance-none bg-paper border border-champagne rounded-soft px-4 py-4 text-espresso font-light focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold shadow-luxurySoft pr-12"
                      >
                        <option value="">Select Package</option>
                        {packageOptions.map((pkg, i) => (
                          <option key={i} value={pkg}>{pkg}</option>
                        ))}
                      </select>
                      <label className="absolute left-4 -top-4 text-[12px] uppercase tracking-[0.08em] text-muted font-medium">
                        Package Interest
                      </label>
                      <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gold pointer-events-none" />
                    </div>
                    <div className="relative luxury-select-wrap min-w-0">
                      <select
                        id="timeline"
                        value={formData.timeline}
                        onChange={handleInputChange}
                        className="luxury-select-control w-full appearance-none bg-paper border border-champagne rounded-soft px-4 py-4 text-espresso font-light focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold shadow-luxurySoft pr-12"
                      >
                        <option value="">Select Timeline</option>
                        <option value="ASAP">ASAP</option>
                        <option value="This month">This month</option>
                        <option value="Planning ahead">Planning ahead</option>
                      </select>
                      <label className="absolute left-4 -top-4 text-[12px] uppercase tracking-[0.08em] text-muted font-medium">
                        Timeline
                      </label>
                      <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gold pointer-events-none" />
                    </div>
                  </div>

                  <div className="relative mt-8">
                    <textarea
                      id="message"
                      rows="3"
                      placeholder=" "
                      value={formData.message}
                      onChange={handleInputChange}
                      className="peer w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent resize-none text-espresso"
                    ></textarea>
                    <label htmlFor="message" className="absolute left-0 -top-3.5 text-[10px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-[10px] peer-focus:text-gold font-medium">
                      Project Details & Requirements
                    </label>
                  </div>

                  <div className="pt-4">
                    <label className="flex items-start gap-4 cursor-pointer group">
                      <input
                        type="checkbox"
                        id="marketingConsent"
                        required
                        checked={formData.marketingConsent}
                        onChange={handleInputChange}
                        className="mt-1 w-4 h-4 accent-gold cursor-pointer rounded-sm border-gold/50"
                      />
                      <span className="text-xs font-light text-muted leading-relaxed group-hover:text-espresso transition-colors">
                        {consentTextStr}
                      </span>
                    </label>
                  </div>

                  <div className="flex flex-col md:flex-row items-center gap-8 pt-6 border-t border-champagne/50">
                    <div className="flex items-center gap-4 w-full md:w-auto">
                      <span className="text-xs font-medium uppercase tracking-widest text-muted whitespace-nowrap">
                        Security Check: {mathA} + {mathB} =
                      </span>
                      <input
                        type="number"
                        id="mathAnswer"
                        required
                        value={formData.mathAnswer}
                        onChange={handleInputChange}
                        className="w-16 bg-paper border border-gold/30 rounded-md py-2 text-center text-espresso focus:outline-none focus:border-gold shadow-inner"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={formState === 'submitting'}
                      className="flex-grow luxury-gold-button w-full md:w-auto disabled:opacity-70 disabled:cursor-not-allowed luxury-gold-sweep"
                    >
                      Submit Inquiry
                    </button>
                  </div>

                  {errorMessage && (
                    <div className="flex items-center gap-2 text-red-600 text-xs mt-4 bg-red-50 p-4 border border-red-200 rounded-soft shadow-sm">
                      <SafeIcon icon={FiAlertCircle} className="flex-shrink-0" />
                      {errorMessage}
                    </div>
                  )}
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Contact;